import { ReactNode, useEffect, useState } from 'react';
import {
  fetchInspectionSheet,
  fetchManagementStandard,
} from '../../api/manufacturingEquipment';
import {
  downloadAcceptanceInspectionAttachment,
  fetchAcceptanceInspection,
} from '../../api/acceptanceInspection';
import {
  downloadMidInspectionAttachment,
  fetchMidInspection,
} from '../../api/midInspection';
import {
  downloadFinalInspectionAttachment,
  fetchFinalInspection,
} from '../../api/finalInspection';
import { fetchCleanEvaluation, fetchCleanSheet } from '../../api/cleanActivity';
import { fetchWorkReport } from '../../api/workReport';
import { fetchLeaveRequest } from '../../api/leaveRequest';
import { fetchMeetingMinutes } from '../../api/meetingMinutes';
import { fetchInternalDraft } from '../../api/internalDraft';
import { fetchOfficialDocument } from '../../api/officialDocument';
import { fetchImprovementSuggestion } from '../../api/improvementSuggestion';
import { fetchPurchaseOrder } from '../../api/purchaseOrder';
import { fetchComplaintAsCase } from '../../api/complaintAs';
import { fetchNonconformanceReport } from '../../api/nonconformanceReport';
import { fetchProductionDailyReport } from '../../api/productionDailyReport';
import { fetchDrawing } from '../../api/drawing';
import { fetchProcessChart } from '../../api/processChart';
import { fetchProductManual } from '../../api/productManual';
import { fetchSafetyCheckSheet, fetchSafetyPpeIssuance } from '../../api/safety';
import {
  fetchEducationPlan,
  fetchEducationRecord,
  fetchExternalEducationReport,
} from '../../api/education';
import { fetchExternalAudit } from '../../api/externalAudit';
import {
  fetchInternalAuditChecklist,
  fetchInternalAuditPlan,
  fetchInternalAuditReport,
} from '../../api/internalAudit';
import {
  fetchSmallGroup,
  fetchSmallGroupActivity,
  fetchSmallGroupEvaluation,
  fetchSmallGroupPlan,
} from '../../api/smallGroup';
import { fetchQualityPolicyCycle } from '../../api/qualityPolicy';
import {
  APPROVAL_TARGET_ACCEPTANCE_INSPECTION,
  APPROVAL_TARGET_CLEAN_ACTIVITY_EVALUATION,
  APPROVAL_TARGET_CLEAN_ACTIVITY_SHEET,
  APPROVAL_TARGET_COMPLAINT_AS,
  APPROVAL_TARGET_DRAWING,
  APPROVAL_TARGET_EDUCATION_ANNUAL_PLAN,
  APPROVAL_TARGET_EDUCATION_RECORD,
  APPROVAL_TARGET_EXTERNAL_AUDIT,
  APPROVAL_TARGET_EXTERNAL_EDUCATION,
  APPROVAL_TARGET_FINAL_INSPECTION,
  APPROVAL_TARGET_IMPROVEMENT_SUGGESTION,
  APPROVAL_TARGET_INSPECTION_SHEET,
  APPROVAL_TARGET_INTERNAL_AUDIT_CHECKLIST,
  APPROVAL_TARGET_INTERNAL_AUDIT_PLAN,
  APPROVAL_TARGET_INTERNAL_AUDIT_REPORT,
  APPROVAL_TARGET_INTERNAL_DRAFT,
  APPROVAL_TARGET_LEAVE,
  APPROVAL_TARGET_MANAGEMENT_STANDARD,
  APPROVAL_TARGET_MEETING_MINUTES,
  APPROVAL_TARGET_MID_INSPECTION,
  APPROVAL_TARGET_NCR,
  APPROVAL_TARGET_OFFICIAL_DOCUMENT,
  APPROVAL_TARGET_PROCESS_CHART,
  APPROVAL_TARGET_PRODUCT_MANUAL,
  APPROVAL_TARGET_PRODUCTION_DAILY,
  APPROVAL_TARGET_PURCHASE_ORDER,
  APPROVAL_TARGET_QUALITY_POLICY_DETAIL,
  APPROVAL_TARGET_QUALITY_POLICY_PLAN,
  APPROVAL_TARGET_QUALITY_POLICY_REPORT,
  APPROVAL_TARGET_SAFETY_CHECK,
  APPROVAL_TARGET_SAFETY_PPE,
  APPROVAL_TARGET_SMALL_GROUP,
  APPROVAL_TARGET_SMALL_GROUP_EVAL,
  APPROVAL_TARGET_SMALL_GROUP_PLAN,
  APPROVAL_TARGET_SMALL_GROUP_RECORD,
  APPROVAL_TARGET_WORK_REPORT,
  ApprovalTargetInfo,
} from '../../types/approval';
import {
  acceptanceItemLabel,
  isMediaAttachment,
  isRemarkAttachment,
} from '../../types/acceptanceInspection';
import { EquipmentType } from '../../types/manufacturingEquipment';
import AcceptanceInspectionSheet from '../product/AcceptanceInspectionSheet';
import MidInspectionSheet from '../product/MidInspectionSheet';
import FinalInspectionSheet from '../product/FinalInspectionSheet';
import CleanActivitySheetView from '../quality/CleanActivitySheetView';
import CleanActivityEvalView from '../quality/CleanActivityEvalView';
import LeavePreviewView from './previews/LeavePreviewView';
import WorkReportPreviewView from './previews/WorkReportPreviewView';
import MeetingMinutesPreviewView from './previews/MeetingMinutesPreviewView';
import OfficialDocumentPreviewView from './previews/OfficialDocumentPreviewView';
import InternalDraftPreviewView from './previews/InternalDraftPreviewView';
import ImprovementSuggestionPreviewView from './previews/ImprovementSuggestionPreviewView';
import PurchaseOrderPreviewView from './previews/PurchaseOrderPreviewView';
import NcrPreviewView from './previews/NcrPreviewView';
import ComplaintAsPreviewView from './previews/ComplaintAsPreviewView';
import ProductionDailyPreviewView from './previews/ProductionDailyPreviewView';
import DrawingPreviewView from './previews/DrawingPreviewView';
import ProcessChartPreviewView from './previews/ProcessChartPreviewView';
import ProductManualPreviewView from './previews/ProductManualPreviewView';
import SafetyPpePreviewView from './previews/SafetyPpePreviewView';
import SafetyCheckPreviewView from './previews/SafetyCheckPreviewView';
import EducationPlanPreviewView from './previews/EducationPlanPreviewView';
import EducationRecordPreviewView from './previews/EducationRecordPreviewView';
import ExternalEducationPreviewView from './previews/ExternalEducationPreviewView';
import ExternalAuditPreviewView from './previews/ExternalAuditPreviewView';
import InternalAuditPlanPreviewView from './previews/InternalAuditPlanPreviewView';
import InternalAuditChecklistPreviewView from './previews/InternalAuditChecklistPreviewView';
import InternalAuditReportPreviewView from './previews/InternalAuditReportPreviewView';
import SmallGroupPreviewView from './previews/SmallGroupPreviewView';
import SmallGroupPlanPreviewView from './previews/SmallGroupPlanPreviewView';
import SmallGroupRecordPreviewView from './previews/SmallGroupRecordPreviewView';
import SmallGroupEvalPreviewView from './previews/SmallGroupEvalPreviewView';
import QualityPolicyPreviewView from './previews/QualityPolicyPreviewView';
import ManagementStandardPreviewView from './previews/ManagementStandardPreviewView';
import InspectionSheetPreviewView from './previews/InspectionSheetPreviewView';
import './ApprovalTargetPreview.css';
import { reportError } from '../../utils/notify';

interface Props {
  targetType: string;
  targetId?: number | null;
  targetInfo?: ApprovalTargetInfo | null;
}

function toEquipType(value?: string | null): EquipmentType {
  return value === 'INSPECTION' ? 'INSPECTION' : 'MANUFACTURING';
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName || 'attachment';
  a.click();
  URL.revokeObjectURL(url);
}

function requireTargetId(targetId?: number | null): number {
  if (targetId == null) {
    throw new Error('MISSING_TARGET_ID');
  }
  return targetId;
}

async function loadPreviewContent(
  targetType: string,
  targetId?: number | null,
  targetInfo?: ApprovalTargetInfo | null
): Promise<ReactNode> {
  switch (targetType) {
    case APPROVAL_TARGET_LEAVE:
      return <LeavePreviewView detail={await fetchLeaveRequest(requireTargetId(targetId))} />;

    case APPROVAL_TARGET_WORK_REPORT:
      return <WorkReportPreviewView detail={await fetchWorkReport(requireTargetId(targetId))} />;

    case APPROVAL_TARGET_MEETING_MINUTES:
      return (
        <MeetingMinutesPreviewView detail={await fetchMeetingMinutes(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_INTERNAL_DRAFT:
      return (
        <InternalDraftPreviewView detail={await fetchInternalDraft(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_OFFICIAL_DOCUMENT:
      return (
        <OfficialDocumentPreviewView
          detail={await fetchOfficialDocument(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_IMPROVEMENT_SUGGESTION:
      return (
        <ImprovementSuggestionPreviewView
          detail={await fetchImprovementSuggestion(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_PURCHASE_ORDER:
      return (
        <PurchaseOrderPreviewView detail={await fetchPurchaseOrder(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_COMPLAINT_AS:
      return (
        <ComplaintAsPreviewView detail={await fetchComplaintAsCase(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_NCR:
      return (
        <NcrPreviewView detail={await fetchNonconformanceReport(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_PRODUCTION_DAILY:
      return (
        <ProductionDailyPreviewView
          detail={await fetchProductionDailyReport(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_DRAWING:
      return <DrawingPreviewView detail={await fetchDrawing(requireTargetId(targetId))} />;

    case APPROVAL_TARGET_PROCESS_CHART:
      return <ProcessChartPreviewView detail={await fetchProcessChart(requireTargetId(targetId))} />;

    case APPROVAL_TARGET_PRODUCT_MANUAL:
      return (
        <ProductManualPreviewView detail={await fetchProductManual(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_SAFETY_PPE:
      return (
        <SafetyPpePreviewView detail={await fetchSafetyPpeIssuance(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_SAFETY_CHECK:
      return (
        <SafetyCheckPreviewView detail={await fetchSafetyCheckSheet(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_EDUCATION_ANNUAL_PLAN:
      return (
        <EducationPlanPreviewView detail={await fetchEducationPlan(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_EDUCATION_RECORD:
      return (
        <EducationRecordPreviewView detail={await fetchEducationRecord(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_EXTERNAL_EDUCATION:
      return (
        <ExternalEducationPreviewView
          detail={await fetchExternalEducationReport(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_EXTERNAL_AUDIT:
      return (
        <ExternalAuditPreviewView detail={await fetchExternalAudit(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_INTERNAL_AUDIT_PLAN:
      return (
        <InternalAuditPlanPreviewView
          detail={await fetchInternalAuditPlan(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_INTERNAL_AUDIT_CHECKLIST:
      return (
        <InternalAuditChecklistPreviewView
          detail={await fetchInternalAuditChecklist(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_INTERNAL_AUDIT_REPORT:
      return (
        <InternalAuditReportPreviewView
          detail={await fetchInternalAuditReport(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_SMALL_GROUP:
      return <SmallGroupPreviewView detail={await fetchSmallGroup(requireTargetId(targetId))} />;

    case APPROVAL_TARGET_SMALL_GROUP_PLAN:
      return (
        <SmallGroupPlanPreviewView detail={await fetchSmallGroupPlan(requireTargetId(targetId))} />
      );

    case APPROVAL_TARGET_SMALL_GROUP_RECORD:
      return (
        <SmallGroupRecordPreviewView
          detail={await fetchSmallGroupActivity(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_SMALL_GROUP_EVAL:
      return (
        <SmallGroupEvalPreviewView
          detail={await fetchSmallGroupEvaluation(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_QUALITY_POLICY_PLAN:
    case APPROVAL_TARGET_QUALITY_POLICY_DETAIL:
    case APPROVAL_TARGET_QUALITY_POLICY_REPORT: {
      const cycle = await fetchQualityPolicyCycle(requireTargetId(targetId));
      const doc =
        targetType === APPROVAL_TARGET_QUALITY_POLICY_PLAN
          ? 'plan'
          : targetType === APPROVAL_TARGET_QUALITY_POLICY_DETAIL
            ? 'detail'
            : 'report';
      return <QualityPolicyPreviewView cycle={cycle} doc={doc} />;
    }

    case APPROVAL_TARGET_CLEAN_ACTIVITY_SHEET:
      return <CleanActivitySheetView sheet={await fetchCleanSheet(requireTargetId(targetId))} />;

    case APPROVAL_TARGET_CLEAN_ACTIVITY_EVALUATION:
      return (
        <CleanActivityEvalView
          evaluation={await fetchCleanEvaluation(requireTargetId(targetId))}
        />
      );

    case APPROVAL_TARGET_ACCEPTANCE_INSPECTION:
    case APPROVAL_TARGET_MID_INSPECTION:
    case APPROVAL_TARGET_FINAL_INSPECTION: {
      const id = targetId ?? targetInfo?.inspectionId;
      if (id == null) {
        throw new Error('MISSING_TARGET_ID');
      }
      if (targetType === APPROVAL_TARGET_MID_INSPECTION) {
        const mid = await fetchMidInspection(id);
        return (
          <div className="approval-preview approval-preview--doc">
            <div className="approval-preview__head">
              <h3>중간검사성적서</h3>
              <p>
                {mid.formTitle || '-'} · {mid.itemName || '-'}
              </p>
            </div>
            <div className="approval-preview__doc">
              <MidInspectionSheet
                values={{
                  inventoryId: mid.inventoryId,
                  itemKind: mid.itemKind ?? '',
                  itemName: mid.itemName ?? '',
                  specText: mid.specText ?? '',
                  authorName: mid.authorName ?? '',
                  reviewerName: mid.reviewerName ?? '',
                  approverName: mid.approverName ?? '',
                  lotNo: mid.lotNo ?? '',
                  lotQty: mid.lotQty ?? '',
                  inspectorName: mid.inspectorName ?? '',
                  inspectionDate: mid.inspectionDate?.slice(0, 10) ?? '',
                  appearanceRemark: mid.appearanceRemark ?? '',
                  appearanceJudgment: mid.appearanceJudgment ?? '',
                  electricalRemark: mid.electricalRemark ?? '',
                  electricalJudgment: mid.electricalJudgment ?? '',
                  agingRemark: mid.agingRemark ?? '',
                  agingJudgment: mid.agingJudgment ?? '',
                  overallJudgment: mid.overallJudgment ?? '',
                  remark: mid.remark ?? '',
                  items: mid.items ?? [],
                  inputs: mid.inputs ?? [],
                }}
                remarkAttachments={mid.attachments ?? []}
                onDownloadAttachment={(file) => {
                  void downloadMidInspectionAttachment(mid.inspectionId, file.attachmentId)
                    .then((blob) => downloadBlob(blob, file.fileName))
                    .catch(() => undefined);
                }}
              />
            </div>
          </div>
        );
      }
      if (targetType === APPROVAL_TARGET_FINAL_INSPECTION) {
        const finalInspection = await fetchFinalInspection(id);
        return (
          <div className="approval-preview approval-preview--doc">
            <div className="approval-preview__head">
              <h3>제품검사성적서</h3>
              <p>
                {finalInspection.formTitle || '-'} · {finalInspection.itemName || '-'}
              </p>
            </div>
            <div className="approval-preview__doc">
              <FinalInspectionSheet
                values={{
                  inventoryId: finalInspection.inventoryId,
                  itemKind: finalInspection.itemKind ?? '',
                  itemName: finalInspection.itemName ?? '',
                  specText: finalInspection.specText ?? '',
                  authorName: finalInspection.authorName ?? '',
                  reviewerName: finalInspection.reviewerName ?? '',
                  approverName: finalInspection.approverName ?? '',
                  lotNo: finalInspection.lotNo ?? '',
                  lotQty: finalInspection.lotQty ?? '',
                  inspectorName: finalInspection.inspectorName ?? '',
                  inspectionDate: finalInspection.inspectionDate?.slice(0, 10) ?? '',
                  overallJudgment: finalInspection.overallJudgment ?? '',
                  remark: finalInspection.remark ?? '',
                  items: finalInspection.items ?? [],
                }}
                remarkAttachments={finalInspection.attachments ?? []}
                onDownloadAttachment={(file) => {
                  void downloadFinalInspectionAttachment(
                    finalInspection.inspectionId,
                    file.attachmentId
                  )
                    .then((blob) => downloadBlob(blob, file.fileName))
                    .catch(() => undefined);
                }}
              />
            </div>
          </div>
        );
      }
      const acceptance = await fetchAcceptanceInspection(id);
      return (
        <div className="approval-preview approval-preview--doc">
          <div className="approval-preview__head">
            <h3>인수검사성적서</h3>
            <p>
              {acceptance.formTitle || '-'} · {acceptanceItemLabel(acceptance)}
            </p>
          </div>
          <div className="approval-preview__doc">
            <AcceptanceInspectionSheet
              values={{
                inventoryId: acceptance.inventoryId ?? null,
                itemKind: acceptance.itemKind || '',
                productName:
                  acceptanceItemLabel(acceptance) === '-' ? '' : acceptanceItemLabel(acceptance),
                authorName: acceptance.authorName || '',
                reviewerName: acceptance.reviewerName || '',
                approverName: acceptance.approverName || '',
                specText: acceptance.specText || '',
                lotNo: acceptance.lotNo || '',
                material: acceptance.material || '',
                incomingQty: acceptance.incomingQty || '',
                inspectorName: acceptance.inspectorName || '',
                vendorName: acceptance.vendorName || '',
                vendorContactName: acceptance.vendorContactName || '',
                vendorPhone: acceptance.vendorPhone || '',
                incomingDate: acceptance.incomingDate?.slice(0, 10) || '',
                inspectionDate: acceptance.inspectionDate?.slice(0, 10) || '',
                overallJudgment: acceptance.overallJudgment || '',
                remark: acceptance.remark || '',
                items: acceptance.items ?? [],
              }}
              mediaAttachments={(acceptance.attachments ?? []).filter(isMediaAttachment)}
              remarkAttachments={(acceptance.attachments ?? []).filter(isRemarkAttachment)}
              onDownloadAttachment={(file) => {
                void downloadAcceptanceInspectionAttachment(
                  acceptance.inspectionId,
                  file.attachmentId
                )
                  .then((blob) => downloadBlob(blob, file.fileName))
                  .catch(() => undefined);
              }}
            />
          </div>
        </div>
      );
    }

    case APPROVAL_TARGET_MANAGEMENT_STANDARD:
    case APPROVAL_TARGET_INSPECTION_SHEET: {
      const equipmentId = targetInfo?.equipmentId;
      if (!equipmentId) {
        throw new Error('MISSING_TARGET_ID');
      }
      const type = toEquipType(targetInfo?.equipmentType);
      if (targetType === APPROVAL_TARGET_MANAGEMENT_STANDARD) {
        return (
          <ManagementStandardPreviewView
            standard={await fetchManagementStandard(equipmentId, type)}
          />
        );
      }
      const year = targetInfo?.sheetYear;
      if (!year) {
        throw new Error('MISSING_SHEET_YEAR');
      }
      return (
        <InspectionSheetPreviewView
          sheet={await fetchInspectionSheet(equipmentId, year, type)}
        />
      );
    }

    default:
      throw new Error('UNSUPPORTED_TARGET');
  }
}

function ApprovalTargetPreview({ targetType, targetId, targetInfo }: Props) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [content, setContent] = useState<ReactNode>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        setLoading(true);
        setError('');
        setContent(null);
        const next = await loadPreviewContent(targetType, targetId, targetInfo);
        if (!cancelled) {
          setContent(next);
        }
      } catch (err) {
        if (cancelled) return;
        if (err instanceof Error && err.message === 'MISSING_TARGET_ID') {
          if (targetInfo?.summary || targetInfo?.formTitle) {
            setContent(
              <div className="approval-preview">
                <div className="approval-preview__head">
                  <h3>{targetInfo.formTitle || '결재 대상'}</h3>
                  <p>{targetInfo.summary || '-'}</p>
                </div>
              </div>
            );
            return;
          }
          reportError(setError, '대상 문서 정보가 없습니다.');
        } else if (err instanceof Error && err.message === 'MISSING_SHEET_YEAR') {
          reportError(setError, '점검시트 연도 정보가 없습니다.');
        } else if (err instanceof Error && err.message === 'UNSUPPORTED_TARGET') {
          if (targetInfo?.summary || targetInfo?.formTitle) {
            setContent(
              <div className="approval-preview">
                <div className="approval-preview__head">
                  <h3>{targetInfo.formTitle || '결재 대상'}</h3>
                  <p>{targetInfo.summary || '-'}</p>
                </div>
              </div>
            );
            return;
          }
          reportError(setError, '지원하지 않는 결재 대상입니다.');
        } else {
          reportError(setError, '대상 문서를 불러오지 못했습니다.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [targetType, targetId, targetInfo]);

  if (loading) {
    return <p className="approval-preview__hint">대상 문서 불러오는 중...</p>;
  }
  if (error) {
    return <p className="approval-preview__error">{error}</p>;
  }
  if (content) {
    return <div className="approval-preview approval-preview--form">{content}</div>;
  }
  return <p className="approval-preview__hint">표시할 대상 문서가 없습니다.</p>;
}

export default ApprovalTargetPreview;
